import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, Button, Box, Pagination, Typography } from '@mui/material';
import ModalFrame from '../../../components/_commons/ModalFrame';
import BatchRemoverCreateModal from '../../../components/services/csmsmedia/bulkDeleter/BulkDeleterCreateModal';
import BatchRemoverEditModal from '../../../components/services/csmsmedia/bulkDeleter/BulkDeleterEditModal';
import BatchRemoverDeleteModal from '../../../components/services/csmsmedia/bulkDeleter/BatchRemoverDeleteModal';
import LoadingComponent from '../../../components/_commons/LoadingComponent';
import '@resources/assets/styles/bulkDeleter.scss';
import api from '@utils/axiosInstance';

function BulkDeleter(props) {
    const organizationData = useSelector(state => state.organization.organizationData);
    const userToken = useSelector(state => state.organization.userToken);
    const { t } = useTranslation();

    const [batchList, setBatchList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [orderBy, setOrderBy] = useState('created_at'); // 기본 정렬 기준을 'created_at'으로 설정
    const [orderDirection, setOrderDirection] = useState('desc'); // 기본 정렬 방향을 'desc'로 설정
    const [page, setPage] = useState(1); // 페이지는 1부터 시작
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [noData, setNoData] = useState(false);
    const [editUuid, setEditUuid] = useState('');
    const [editDate, setEditDate] = useState({
        startDate: '',
        endDate: ''
    });
    const [deleteUuid, setDeleteUuid] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [openExampleModal, setOpenExampleModal] = useState(false);
    const handleExample = () => {
        setOpenExampleModal(true);
    }

    const handleOpen = () => setOpenCreateModal(true);
    const handleEdit = (uuid, startDate, endDate) => { setOpenEditModal(true), setEditUuid(uuid), setEditDate({ startDate: startDate, endDate: endDate }) };
    const handleDelete = (uuid) => { setOpenDeleteModal(true), setDeleteUuid(uuid) };

    useEffect(() => {
        const fetchBatchList = async (page) => {
            try {
                const response = await api({
                    method: 'post',
                    url: 'bulkDeleter/getList',
                    data: {
                        tk: userToken,
                        ogc: organizationData.organization_code,
                        type: "vod",
                        page: page,
                    }
                });

                if (response.data.code === 100) {
                    setBatchList(response.data.list.data);
                    setTotalCount(response.data.list.totalItems);
                    setIsLoading(false);

                    if (response.data.list.data.length > 0) {
                        setNoData(true);
                    }
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        };

        if (page && userToken && organizationData.organization_code) {
            setIsLoading(true);
            fetchBatchList(page);
        }
    }, [page, userToken, organizationData.organization_code]);

    // 데이터 정렬 함수
    const sortedData = React.useMemo(() => {
        return [...batchList].sort((a, b) => {
            if (orderBy === 'created_at') {
                return orderDirection === 'asc'
                    ? new Date(a.created_at) - new Date(b.created_at)
                    : new Date(b.created_at) - new Date(a.created_at);
            }
            if (a[orderBy] < b[orderBy]) return orderDirection === 'asc' ? -1 : 1;
            if (a[orderBy] > b[orderBy]) return orderDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [batchList, orderBy, orderDirection]);

    const paginatedData = sortedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && orderDirection === 'asc';
        setOrderDirection(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // 페이지 변경 핸들러
    const handleChangePage = (event, value) => {
        setPage(value);
    };

    return (
        <div className='container-video-batchRemover'>
            {
                !isLoading ?
                    <div className="container-video-batchRemover-wrapper">
                        <Box>
                            <Box className="bulkdeleter-btn-row">
                                <Button variant="contained" color="primary" onClick={handleOpen}>
                                    <Typography variant="button" className="bulkdeleter-btn-text">
                                        {t('vod.bulk_deleter.delete_request')}
                                    </Typography>
                                </Button>
                            </Box>
                            <Paper className="bulkdeleter-table-paper">
                                <TableContainer>
                                    <Table id="table-batch-remover">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'id'}
                                                        direction={orderBy === 'id' ? orderDirection : 'asc'}
                                                        onClick={() => handleRequestSort('id')}
                                                    >
                                                        <Typography variant="body1" className="bulkdeleter-table-header">
                                                            #
                                                        </Typography>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'username'}
                                                        direction={orderBy === 'username' ? orderDirection : 'asc'}
                                                        onClick={() => handleRequestSort('username')}
                                                    >
                                                        <Typography variant="body1" className="bulkdeleter-table-header">
                                                            {t('vod.bulk_deleter.requester')}
                                                        </Typography>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body1" className="bulkdeleter-table-header">
                                                        {t('vod.bulk_deleter.timezone')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'created_at'}
                                                        direction={orderBy === 'created_at' ? orderDirection : 'desc'}
                                                        onClick={() => handleRequestSort('created_at')}
                                                    >
                                                        <Typography variant="body1" className="bulkdeleter-table-header">
                                                            {t('vod.bulk_deleter.request_time')}
                                                        </Typography>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body1" className="bulkdeleter-table-header">
                                                        {t('vod.bulk_deleter.delete_range')} (KST)
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={orderBy === 'target_count'}
                                                        direction={orderBy === 'target_count' ? orderDirection : 'asc'}
                                                        onClick={() => handleRequestSort('target_count')}
                                                    >
                                                        <Typography variant="body1" className="bulkdeleter-table-header">
                                                            {t('vod.bulk_deleter.target_count')}
                                                        </Typography>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body1" className="bulkdeleter-table-header">
                                                        {t('vod.bulk_deleter.status')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="bulkdeleter-action-cell">
                                                    <Typography variant="body1" className="bulkdeleter-table-header">
                                                        {t('vod.bulk_deleter.edit')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="bulkdeleter-action-cell">
                                                    <Typography variant="body1" className="bulkdeleter-table-header">
                                                        {t('vod.bulk_deleter.delete')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedData.map((row) => (
                                                <TableRow key={row.id}>
                                                    <TableCell className="bulkdeleter-table-cell">{row.id}</TableCell>
                                                    <TableCell className="bulkdeleter-table-cell">{row.username}</TableCell>
                                                    <TableCell className="bulkdeleter-table-cell">{row.timezone.toUpperCase()}</TableCell>
                                                    <TableCell className="bulkdeleter-table-cell">{new Date(row.created_at).toLocaleString('ko-KR')}</TableCell>
                                                    <TableCell className="bulkdeleter-table-cell">
                                                        {new Date(row.start_date).toLocaleDateString('ko-KR')} ~ {new Date(row.end_date).toLocaleDateString('ko-KR')}
                                                    </TableCell>
                                                    <TableCell className="bulkdeleter-table-cell">{row.target_count}</TableCell>
                                                    <TableCell className="bulkdeleter-table-cell">{row.status || '-'}</TableCell>
                                                    <TableCell className="bulkdeleter-action-cell">
                                                        <Button
                                                            variant="contained"
                                                            color="warning"
                                                            size="small"
                                                            onClick={() => handleEdit(row.uuid, row.start_date, row.end_date)}
                                                            className="bulkdeleter-edit-btn"
                                                        >
                                                            <Typography variant="button" className="bulkdeleter-btn-text">
                                                                {t('buttons.edit')}
                                                            </Typography>
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell className="bulkdeleter-action-cell">
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleDelete(row.uuid)}
                                                        >
                                                            <Typography variant="button" className="bulkdeleter-btn-text">
                                                                {t('buttons.delete')}
                                                            </Typography>
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {/* 페이지네이션 추가 */}
                                <Box className="bulkdeleter-pagination-row">
                                    <Pagination
                                        count={Math.ceil(totalCount / rowsPerPage)} // 총 페이지 수 계산
                                        page={page}
                                        onChange={handleChangePage}
                                        color="primary"
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </div>
                    :
                    noData ?
                        <div className="bulkdeleter-no-data-container">
                            <Typography variant="h6">{t('vod.bulk_deleter.no_request')}</Typography>
                        </div>
                        :
                        <LoadingComponent />
            }

            <ModalFrame
                open={openExampleModal}
                onClose={() => setOpenExampleModal(false)}
                title={t('vod.bulk_deleter.example_title')}
            >
                <Typography variant="body1">{t('vod.bulk_deleter.example_here')}</Typography>
            </ModalFrame>
            <BatchRemoverCreateModal open={openCreateModal} setOpen={setOpenCreateModal} />
            <BatchRemoverEditModal open={openEditModal} setOpen={setOpenEditModal} uuid={editUuid} date={editDate} />
            <BatchRemoverDeleteModal open={openDeleteModal} setOpen={setOpenDeleteModal} uuid={deleteUuid} />
        </div>
    );
}

export default BulkDeleter;